/**
 * AppUpdateBanner - "a new version is available, reload" toast (task #200).
 *
 * Why this exists: the SPA shell is served behind a CDN with a long `s-maxage`,
 * so a returning browser can keep running an OLD hashed-chunk bundle after a
 * deploy until a hard refresh. That repeatedly stranded the user on stale code
 * (the "zone overlay still broken" / "zones failed to load" reports were both
 * fixed in shipped builds the user had not yet picked up). Instead of trusting
 * cache headers to be perfect, the running app ACTIVELY notices a newer build
 * and offers a one-click reload.
 *
 * Detection (see src/lib/app-version.ts):
 *   - `APP_BUILD_ID` is baked into this bundle at build time.
 *   - `/version.json` (emitted by the vite plugin) carries the deployed build
 *     id; it is fetched with cache-busting + `no-store` so neither the browser
 *     nor the CDN can return a stale value.
 *   - When the deployed id differs from the baked id, this banner appears.
 *
 * Cadence: an immediate check on mount, then every UPDATE_CHECK_INTERVAL_MS,
 * AND whenever the tab regains visibility / focus (so a user returning to a
 * long-idle tab is told promptly). Each is cheap (one small JSON GET).
 *
 * Deliberate choices:
 *   - NOT auto-reload: a forced reload could interrupt the user mid-task, so we
 *     prompt and let them choose.
 *   - Dismiss is per-build-id: dismissing hides THIS version's notice, but a
 *     LATER deploy (new id) re-prompts.
 *   - Quiet in dev / tests: APP_BUILD_ID is 'dev' there, so the poll is skipped
 *     entirely and the banner never shows.
 *   - Mounted at the app entry root (main.tsx) as a sibling of the router, so a
 *     pure-CSS slide-in is used (AppUpdateBanner.css.ts) instead of framer-
 *     motion, keeping framer out of the entry chunk.
 */
import { useCallback, useEffect, useState } from 'react';
import { APP_BUILD_ID, fetchDeployedBuildId, isUpdateAvailable } from '@/lib/app-version';
import * as styles from './AppUpdateBanner.css';

/**
 * How often to re-check the deployed build id while the tab is open. 5 minutes
 * balances "tell the user reasonably soon after a deploy" against not spamming
 * the origin. Visibility/focus checks cover the gaps between intervals.
 */
export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Renders the update toast when a newer bundle has been deployed than the one
 * this tab is running; otherwise renders nothing.
 *
 * @returns The banner element, or null when no update is pending.
 */
export function AppUpdateBanner(): JSX.Element | null {
  const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null);
  const [dismissedForId, setDismissedForId] = useState<string | null>(null);

  useEffect(() => {
    // No real build id (local dev / unit tests) -> nothing to update to.
    if (APP_BUILD_ID === 'dev') return undefined;

    let cancelled = false;
    const check = async (): Promise<void> => {
      const id = await fetchDeployedBuildId(Date.now());
      if (!cancelled && id) setDeployedBuildId(id);
    };

    void check(); // immediate check on mount
    const interval = window.setInterval(() => void check(), UPDATE_CHECK_INTERVAL_MS);
    // Re-check when the user returns to the tab (covers the gap between polls).
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  /** Pick up the new bundle. A reload re-fetches index.html -> new chunks. */
  const onReload = useCallback((): void => {
    window.location.reload();
  }, []);

  /** Hide the notice for THIS build id; a later deploy re-prompts. */
  const onDismiss = useCallback((): void => {
    setDismissedForId(deployedBuildId);
  }, [deployedBuildId]);

  const show =
    isUpdateAvailable(deployedBuildId) && deployedBuildId !== dismissedForId;
  if (!show) return null;

  return (
    <div className={styles.root} data-testid="app-update-banner">
      <div className={styles.card} role="status" aria-live="polite">
        <span className={styles.message}>
          A new version of SignalSentry is available.
        </span>
        <span className={styles.actions}>
          <button
            type="button"
            className={styles.reloadButton}
            onClick={onReload}
            data-testid="app-update-reload"
          >
            Reload
          </button>
          <button
            type="button"
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss update notice"
            data-testid="app-update-dismiss"
          >
            ✕
          </button>
        </span>
      </div>
    </div>
  );
}

export default AppUpdateBanner;
